/**
 * Service pour interroger l'API Elloha (disponibilités, prix à partir de).
 * Doc : https://doc.elloha.com/api/v1/fr/
 *
 * Les appels passent par un proxy (getEllohaUrlFromEnvironment, configuré dans Apache) ; cela permet d'éviter le blocage CORS (l'API Elloha ne renvoie pas Access-Control-Allow-Origin), et de ne pas positionner le header elloha-client-id ici côté front.
 */

import { getEllohaUrlFromEnvironment } from "../utils/widgetsUtils";

/** Réponse produit simplifiée (recherche globale) */
interface EllohaProductResponse {
  IdProduct?: string;
  PriceFrom?: number;
  [key: string]: unknown;
}

function formatDateYyyyMmDd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * URL du proxy Elloha (Apache) : `elloha.apidae-tourisme.com` → `api.elloha.com`.
 */
function getEllohaAvailabilitiesProxyUrl(): string {
  const base = getEllohaUrlFromEnvironment().replace(/\/$/, "");
  const u = new URL(base);
  u.pathname = "/Availabilities";
  u.search = "";
  return u.toString();
}

/**
 * Récupère les prix minimum (PriceFrom) pour plusieurs produits en une requête.
 * Retourne une Map idProduct -> PriceFrom (uniquement les produits avec un prix).
 * Passe par le proxy Elloha (Apache) pour appeler l'API Elloha.
 */
export async function getPriceFromBatch(
  idBookingEngine: string,
  idProducts: string[],
  culture: string,
): Promise<Map<string, number>> {
  if (idProducts.length === 0) return new Map();

  const startDate = formatDateYyyyMmDd(new Date());
  const body = {
    IdBookingEngine: idBookingEngine,
    IdProducts: idProducts,
    StartDate: startDate,
  };

  const headers: Record<string, string> = {
    accept: "application/json",
    "Content-Type": "application/json",
    culture,
  };

  try {
    const res = await fetch(getEllohaAvailabilitiesProxyUrl(), {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) return new Map();

    const data = await res.json();

    if (data?.ErrorCode) return new Map();

    // Elloha peut renvoyer soit un tableau, soit un objet unique (cas d'une requête "single").
    const products: EllohaProductResponse[] = Array.isArray(data)
      ? (data as EllohaProductResponse[])
      : data &&
          typeof data === "object" &&
          (data as EllohaProductResponse).IdProduct
        ? [data as EllohaProductResponse]
        : [];

    const map = new Map<string, number>();
    for (const p of products) {
      const id = p?.IdProduct;
      const price = p?.PriceFrom;
      if (id && typeof price === "number" && Number.isFinite(price)) {
        map.set(id, price);
      }
    }
    return map;
  } catch {
    return new Map();
  }
}
